// ProductRowBlocks — the five row-bound block components. Each reads the
// current product row via useCurrentRow() and renders nothing when there isn't
// one (i.e. outside a product-table cell), the React mirror of the PDF
// renderer's `if (row == null) return;` guards. They reuse the cart's
// `sd-cart__*` look-and-feel classes so a composed table reads identically to
// the legacy fixed cart, while also carrying generic `sd-pt__*` hooks so a
// deployment can restyle them in isolation.
//
// These live in their own file (not Slot.tsx) because the dispatcher only needs
// to instantiate them — the shared row plumbing belongs next to the components
// that consume it.

import { fieldKey, useCurrentRow } from "./RowContext";
import type { BlockUniversalProps } from "./types";

/** Product image — the row's thumbnail, or the cart's placeholder span when the
 *  product carries no image. */
export function ProductImageBlock({ themeVariant, className }: BlockUniversalProps) {
  const row = useCurrentRow();
  if (!row) return null;
  return (
    <div
      className={"sd-pt__cell-img sd-cart__row-img" + (className ? " " + className : "")}
      data-variant={themeVariant}
    >
      {row.imageUrl ? (
        <img src={row.imageUrl} alt={row.name} draggable={false} />
      ) : (
        <span className="sd-cart__row-placeholder" aria-hidden="true" />
      )}
    </div>
  );
}

/** Product name — the row's display name. */
export function ProductNameBlock({ themeVariant, className }: BlockUniversalProps) {
  const row = useCurrentRow();
  if (!row) return null;
  return (
    <div
      className={"sd-pt__cell-name sd-cart__row-name" + (className ? " " + className : "")}
      data-variant={themeVariant}
    >
      {row.name}
    </div>
  );
}

export interface ProductParameterBlockProps extends BlockUniversalProps {
  /** Which value to render. Built-in keys (name / sku / category / series) and
   *  `param:<key>` read like the C# ComposeProductParameter; any other key is
   *  treated as a bare parameter name into row.parameters. Defaults to "name". */
  parameterKey?: string;
}

/** Product parameter — renders one of the row's fields. Mirrors the C#
 *  ComposeProductParameter resolution: the built-in keys keep their meaning and
 *  anything else (or a `param:<key>`) reads the arbitrary parameter map, so a
 *  column can surface ANY product parameter. Empty value → render nothing. */
export function ProductParameterBlock({
  parameterKey = "name",
  themeVariant,
  className,
}: ProductParameterBlockProps) {
  const row = useCurrentRow();
  if (!row) return null;
  // Built-in field names route through fieldKey (same vocabulary as sort/group);
  // a bare key that isn't built-in falls back to a direct parameter read so a
  // column can name a parameter without the "param:" prefix.
  let value: string;
  if (
    parameterKey === "name" ||
    parameterKey === "sku" ||
    parameterKey === "category" ||
    parameterKey === "series" ||
    parameterKey.toLowerCase().startsWith("param:")
  ) {
    value = fieldKey(row, parameterKey);
  } else {
    value = row.parameters[parameterKey] ?? "";
  }
  if (!value) return null;
  return (
    <div
      className={"sd-pt__cell-desc sd-cart__row-desc" + (className ? " " + className : "")}
      data-variant={themeVariant}
    >
      {value}
    </div>
  );
}

/** Product SKU — the resolved display article number. Renders in the same
 *  subtle style as a description line by default, so it reads as just another
 *  line when nested under the Product column (mobile cart). A dedicated
 *  Article-number column can make it prominent by adding the "accent" theme
 *  variant (data-variant="accent") on the block, which restores the headline
 *  treatment. Shows a muted "…" placeholder while the lookup is in flight. */
export function ProductSkuBlock({ themeVariant, className }: BlockUniversalProps) {
  const row = useCurrentRow();
  if (!row) return null;
  return (
    <div
      className={"sd-pt__cell-sku sd-cart__row-sku" + (className ? " " + className : "")}
      data-variant={themeVariant}
    >
      {row.productId ? (
        <span title={row.productId}>{row.productId}</span>
      ) : (
        <span className="sd-cart__row-sku--muted">…</span>
      )}
    </div>
  );
}

/** Product quantity — the aggregated count for the row. */
export function ProductQuantityBlock({ themeVariant, className }: BlockUniversalProps) {
  const row = useCurrentRow();
  if (!row) return null;
  return (
    <div
      className={"sd-pt__cell-qty sd-cart__row-qty" + (className ? " " + className : "")}
      data-variant={themeVariant}
      aria-label={`Quantity ${row.quantity}`}
    >
      {row.quantity}
    </div>
  );
}

export interface ProductPriceBlockProps extends BlockUniversalProps {
  /** Which amount to render: "line-total" (default) or "unit". */
  mode?: "line-total" | "unit";
  /** Translatable note when the whole row is allowance-included. */
  includedAllLabel?: string;
  /** Translatable note suffix for partially included rows ("{n} included"). */
  includedLabel?: string;
}

/** Product price — the row's formatted price from the live breakdown, with a
 *  small note for allowance-included units. Renders nothing when the host has
 *  no price source or the row is unpriced. Mirrors the C# ComposeProductPrice. */
export function ProductPriceBlock({
  mode = "line-total",
  includedAllLabel = "Included",
  includedLabel = "included",
  themeVariant,
  className,
}: ProductPriceBlockProps) {
  const row = useCurrentRow();
  if (!row) return null;
  const value = mode === "unit" ? row.unitPrice : row.lineTotal;
  if (!value) return null;
  const included = row.includedQuantity ?? 0;
  return (
    <div
      className={"sd-pt__cell-price sd-cart__row-price" + (className ? " " + className : "")}
      data-variant={themeVariant}
    >
      <span>{value}</span>
      {included > 0 && (
        <span className="sd-cart__row-price-included">
          {included >= row.quantity ? includedAllLabel : `${included} ${includedLabel}`}
        </span>
      )}
    </div>
  );
}

export interface ProductMaterialsBlockProps extends BlockUniversalProps {
  /** "lines" (default): one pick per line. "inline": joined with `separator`. */
  layout?: "lines" | "inline";
  /** Prefix each material with its surface label ("Wood: Oak"). Default true. */
  showSurface?: boolean;
  /** Inline separator between picks. Default ", ". Ignored in "lines" layout. */
  separator?: string;
}

/** Product materials — the user's chosen finishes for the row, resolved to
 *  display names ("Wood: Oak"). Lists only surfaces the user actually picked.
 *  Renders nothing until the product detail resolves the names (row.materials
 *  empty) or when there were no picks. Mirrors the C# ComposeProductMaterials. */
export function ProductMaterialsBlock({
  layout = "lines",
  showSurface = true,
  separator = ", ",
  themeVariant,
  className,
}: ProductMaterialsBlockProps) {
  const row = useCurrentRow();
  if (!row || row.materials.length === 0) return null;

  const format = (m: { surfaceLabel: string; materialName: string }) =>
    showSurface && m.surfaceLabel ? `${m.surfaceLabel}: ${m.materialName}` : m.materialName;

  const base = "sd-pt__cell-materials sd-cart__row-materials" + (className ? " " + className : "");

  if (layout === "inline") {
    return (
      <div className={base} data-variant={themeVariant}>
        {row.materials.map(format).join(separator)}
      </div>
    );
  }
  return (
    <div className={base} data-variant={themeVariant}>
      {row.materials.map((m, i) => (
        <div key={i} className="sd-pt__cell-materials-line">
          {format(m)}
        </div>
      ))}
    </div>
  );
}
